import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { API } from "../config/apiConfig";
import { portalGradientTextSx } from "../styles/dashboardBrand";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Box,
} from "@mui/material";
import SecurityIcon from "@mui/icons-material/Security";

function VerifyResetCode() {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;

  const [form, setForm] = useState({
    verificationCode: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Redirect if no email
  useEffect(() => {
    if (!email) {
      navigate("/forgot-password");
    }
  }, [email, navigate]);

  const handleChange = (e) => {
    const value = e.target.value.replace(/\D/g, ""); // Only allow digits
    if (value.length <= 6) {
      setForm({ ...form, [e.target.name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!form.verificationCode) {
      setError("Please enter the verification code");
      return;
    }

    if (form.verificationCode.length !== 6) {
      setError("Verification code must be 6 digits");
      return;
    }

    try {
      setLoading(true);

      const payload = {
        email: email,
        verificationCode: form.verificationCode,
      };

      console.log("Verify reset code payload:", payload);

      const res = await axios.post(API.verifyResetCode, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (res.data.success) {
        // Navigate to reset password screen
        navigate("/reset-password", {
          state: {
            email: email,
            verificationCode: form.verificationCode,
          },
        });
      } else {
        setError(res.data.error || "Invalid verification code. Please try again.");
      }
    } catch (err) {
      console.error("❌ Verify reset code error:", err.response?.data || err.message);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "❌ Network error. Please check your connection and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!email) {
    return null; // Will redirect
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: 'linear-gradient(135deg, #4FACFE 0%, #6366F1 40%, #9333EA 72%, #E11D48 100%)',
        position: 'relative',
        padding: 2,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Paper 
        elevation={24} 
        sx={{ 
          p: 5, 
          width: "100%", 
          maxWidth: 450,
          borderRadius: 3,
          bgcolor: 'background.paper',
          backdropFilter: 'blur(20px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Stack spacing={3} alignItems="center">
          {/* Header Icon */}
          <SecurityIcon sx={{ fontSize: 60, color: "primary.main" }} />

          {/* Title */}
          <Typography variant="h4" sx={portalGradientTextSx({ fontWeight: 'bold', textAlign: 'center' })}>
            Enter Verification Code
          </Typography>

          {/* Description */}
          <Typography variant="body1" color="text.secondary" textAlign="center">
            We sent a 6-digit code to <strong>{email}</strong>
          </Typography>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ width: "100%" }}>
              {error}
            </Alert>
          )}

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            <Stack spacing={3}>
              {/* Verification Code Field */}
              <TextField
                name="verificationCode"
                label="Verification Code"
                value={form.verificationCode}
                onChange={handleChange}
                required
                fullWidth
                disabled={loading}
                placeholder="000000"
                inputProps={{
                  maxLength: 6,
                  style: {
                    textAlign: "center",
                    fontSize: "24px",
                    fontWeight: "bold",
                    letterSpacing: "4px",
                  },
                }}
                autoComplete="one-time-code"
              />

              {/* Submit Button */}
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || form.verificationCode.length !== 6}
                fullWidth
                sx={{ py: 1.5 }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Verify Code"
                )}
              </Button>

              {/* Back to Forgot Password */}
              <Button
                variant="text"
                onClick={() => navigate("/forgot-password")}
                disabled={loading}
                sx={{ textTransform: "none" }}
              >
                Back to Forgot Password
              </Button>
            </Stack>
          </Box>

          {/* Help Text */}
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Enter the 6-digit code you received in your email.
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
}

export default VerifyResetCode;
